import type { StaffMemoryCommandOutcome, StaffMemoryFact } from "@lafayette/schemas";
import { useState } from "react";
import { Checkbox } from "../../ui/checkbox";
import { Label } from "../../ui/label";
import type { MemoryCommand, MemoryCommandResult, VersionedCommandName } from "../commands";
import { factValueLabel, kindLabel } from "../labels";
import { MemoryDialog, type Reason, ReasonSelect, useCommandForm } from "./MemoryDialog";
import { ProvenanceBadge } from "./parts";

// The commands that need only a version and a reason: activate, reject, confirm, dispute, resolve, delete.

type Wording = Readonly<{
  title: string;
  description: string;
  confirm: string;
  tone: "primary" | "danger";
}>;

const COMMAND_WORDING: Readonly<Record<VersionedCommandName, Wording>> = {
  activate: {
    title: "Activate this memory?",
    description: "It will be used when Lafayette plans for this customer.",
    confirm: "Activate memory",
    tone: "primary",
  },
  reject: {
    title: "Reject this candidate?",
    description: "The candidate is removed and its value is erased. It will not be used.",
    confirm: "Reject candidate",
    tone: "danger",
  },
  confirm: {
    title: "Confirm this memory?",
    description: "You checked that it is still true. This refreshes its last-confirmed date.",
    confirm: "Confirm memory",
    tone: "primary",
  },
  dispute: {
    title: "Mark this memory as disputed?",
    description: "A disputed memory is not used until the dispute is resolved.",
    confirm: "Mark as disputed",
    tone: "danger",
  },
  resolve: {
    title: "Resolve the dispute?",
    description: "The memory is kept and becomes active again.",
    confirm: "Keep memory active",
    tone: "primary",
  },
  delete: {
    title: "Delete this memory?",
    description:
      "The value is erased and cannot be restored. The history of the memory stays. This cannot be undone.",
    confirm: "Delete memory",
    tone: "danger",
  },
};

type Props = Readonly<{
  command: VersionedCommandName;
  fact: StaffMemoryFact;
  run: (request: MemoryCommand) => Promise<MemoryCommandResult>;
  onDone: (outcome: StaffMemoryCommandOutcome) => void;
  onClose: () => void;
}>;

export function FactCommandDialog({ command, fact, run, onDone, onClose }: Props) {
  const wording = COMMAND_WORDING[command];
  const [reason, setReason] = useState<Reason>("");
  const [reasonError, setReasonError] = useState<string | undefined>();
  const [understood, setUnderstood] = useState(false);
  const [understoodError, setUnderstoodError] = useState<string | undefined>();
  const form = useCommandForm(onDone);
  // The delete is the one command that erases a value for good, so it asks for a second, explicit step.
  const needsUnderstanding = command === "delete";

  function validate() {
    const missingReason = reason === "" ? "Choose a reason." : undefined;
    const missingTick =
      needsUnderstanding && !understood ? "Tick the box to confirm the deletion." : undefined;
    setReasonError(missingReason);
    setUnderstoodError(missingTick);
    return missingReason === undefined && missingTick === undefined;
  }

  function submit() {
    if (reason === "") return;
    void form.submit(() =>
      run({
        command,
        factId: fact.id,
        body: { expectedVersion: fact.version, reasonCode: reason },
      }),
    );
  }

  const fieldError =
    form.failure?.invalidField === "reasonCode" ? form.failure.message : reasonError;

  return (
    <MemoryDialog
      title={wording.title}
      description={wording.description}
      confirmLabel={wording.confirm}
      tone={wording.tone}
      failure={form.failure?.invalidField === "reasonCode" ? null : form.failure}
      pending={form.pending}
      validate={validate}
      onSubmit={submit}
      onClose={onClose}
    >
      <div className="grid justify-items-start gap-1">
        <p className="m-0 text-lg leading-7 font-semibold [overflow-wrap:anywhere]">
          {factValueLabel(fact)}
        </p>
        <p className="m-0 text-sm text-muted-foreground">{kindLabel(fact.kind)}</p>
        <ProvenanceBadge provenance={fact.provenance} />
      </div>
      <ReasonSelect value={reason} onChange={setReason} error={fieldError} />
      {needsUnderstanding ? (
        <div className="grid gap-1">
          <div className="grid min-h-11 grid-cols-[1.25rem_minmax(0,1fr)] items-center gap-x-3 compact:min-h-8">
            <Checkbox
              id="memory-delete-understood"
              checked={understood}
              onChange={(event) => setUnderstood(event.currentTarget.checked)}
              aria-invalid={understoodError ? true : undefined}
              aria-describedby={understoodError ? "memory-delete-understood-error" : undefined}
            />
            <Label
              htmlFor="memory-delete-understood"
              className="flex min-h-11 items-center font-normal compact:min-h-8"
            >
              I understand that this erases the value for good.
            </Label>
          </div>
          {understoodError ? (
            <p
              id="memory-delete-understood-error"
              className="m-0 text-sm leading-5 font-semibold text-destructive"
            >
              <span aria-hidden="true">⚠ </span>
              {understoodError}
            </p>
          ) : null}
        </div>
      ) : null}
    </MemoryDialog>
  );
}
